import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Divider,
  Stack,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  Payments as PaymentsIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import disputeFinancialService from '../../services/disputeFinancialService';

/**
 * Composant d'analyse financière complète pour un dispute
 * 
 * Affiche :
 * - État de la caution (deposit) : capturée ou non, montant disponible, statut Stripe
 * - Remboursements éventuels au renter
 * - Payouts pending ponctionnables (owner + renter)
 * - Montant maximum recouvrable pour une pénalité
 * 
 * Utilisé dans DisputeDetailPage pour aider l'admin à prendre une décision
 */
const DisputeFinancialAnalysis = ({ disputeId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFinancialAnalysis();
  }, [disputeId]);

  const loadFinancialAnalysis = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await disputeFinancialService.getFinancialAnalysis(disputeId);
      setAnalysis(data.data);
    } catch (err) {
      console.error('Error loading financial analysis:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement de l\'analyse financière');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Analyse financière en cours...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!analysis) {
    return null;
  }

  const { deposit_analysis, refund_analysis, payouts_analysis, max_recoverable, currency } = analysis;

  // Helpers de formatage
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  };

  // Les payouts sont TOUJOURS en EUR (Stripe paie toujours en EUR)
  const formatPayoutAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'dd MMM yyyy HH:mm', { locale: fr });
  };

  // Statut visuel de la caution
  const getDepositStatusChip = () => {
    if (!deposit_analysis.exists) {
      return <Chip label="Aucune caution" color="default" size="small" />;
    }

    if (deposit_analysis.status === 'released') {
      return <Chip label="Libérée" color="default" size="small" />;
    }

    if (deposit_analysis.status === 'withheld') {
      return <Chip label="Totalement capturée" color="error" size="small" />;
    }

    if (deposit_analysis.status === 'partially_captured') {
      return <Chip label="Partiellement capturée" color="warning" size="small" />;
    }

    if (deposit_analysis.stripe_details?.can_be_captured) {
      return <Chip label="Disponible pour capture" color="success" size="small" icon={<CheckCircleIcon />} />;
    }

    if (deposit_analysis.stripe_details?.requires_3ds) {
      return <Chip label="3DS requis" color="warning" size="small" icon={<SecurityIcon />} />;
    }

    return <Chip label={deposit_analysis.status_readable || deposit_analysis.status} color="info" size="small" />;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AttachMoneyIcon sx={{ fontSize: 32, mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Analyse Financière - Options de Recouvrement
        </Typography>
      </Box>

      {/* Alerte montant maximum recouvrable */}
      {max_recoverable.has_recoverable_funds ? (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Montant maximum recouvrable : {formatPayoutAmount(max_recoverable.total_amount)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {max_recoverable.sources.length} source(s) disponible(s) pour le recouvrement
          </Typography>
        </Alert>
      ) : (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Aucun fonds recouvrable disponible
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            La caution a été libérée ou aucune source de recouvrement n'est disponible.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Section 1: État de la Caution */}
        <Grid item xs={12} md={6}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <AccountBalanceIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  État de la Caution
                </Typography>
                {getDepositStatusChip()}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {!deposit_analysis.exists ? (
                <Alert severity="info">
                  Aucune caution trouvée pour cette réservation
                </Alert>
              ) : (
                <Stack spacing={2}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell><strong>Montant total</strong></TableCell>
                        <TableCell align="right">{formatAmount(deposit_analysis.amount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Montant capturé</strong></TableCell>
                        <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                          {formatAmount(deposit_analysis.captured_amount)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Montant disponible</strong></TableCell>
                        <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                          {formatAmount(deposit_analysis.available_amount)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell><strong>Statut</strong></TableCell>
                        <TableCell align="right">{deposit_analysis.status_readable || deposit_analysis.status}</TableCell>
                      </TableRow>
                      {deposit_analysis.stripe_details && (
                        <>
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary">
                                Détails Stripe
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Statut Stripe</strong></TableCell>
                            <TableCell align="right">{deposit_analysis.stripe_details.status}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Méthode de capture</strong></TableCell>
                            <TableCell align="right">{deposit_analysis.stripe_details.capture_method}</TableCell>
                          </TableRow>
                          {deposit_analysis.stripe_details.can_be_captured && (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Alert severity="success" sx={{ mt: 1 }}>
                                  <Typography variant="caption">
                                    ✅ Cette caution peut être capturée immédiatement via Stripe
                                  </Typography>
                                </Alert>
                              </TableCell>
                            </TableRow>
                          )}
                          {deposit_analysis.stripe_details.requires_3ds && (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                  <Typography variant="caption">
                                    🔐 Cette caution nécessite une authentification 3DS avant capture
                                  </Typography>
                                </Alert>
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      )}
                      {deposit_analysis.capture_details && (
                        <>
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary">
                                Détails de la capture
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Méthode</strong></TableCell>
                            <TableCell align="right">
                              <Chip 
                                label={deposit_analysis.capture_details.method === 'capture' ? 'Capture normale' : 'Off-session'} 
                                size="small" 
                                color={deposit_analysis.capture_details.method === 'capture' ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Capturé le</strong></TableCell>
                            <TableCell align="right">{formatDate(deposit_analysis.capture_details.captured_at)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Montant brut</strong></TableCell>
                            <TableCell align="right">{formatAmount(deposit_analysis.capture_details.amount_captured)}</TableCell>
                          </TableRow>
                          {deposit_analysis.capture_details.fee_amount > 0 && (
                            <>
                              <TableRow>
                                <TableCell><strong>Frais Stripe</strong></TableCell>
                                <TableCell align="right" sx={{ color: 'warning.main' }}>
                                  - {formatAmount(deposit_analysis.capture_details.fee_amount)}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell><strong>Montant net</strong></TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>
                                  {formatAmount(deposit_analysis.capture_details.net_amount)}
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                        </>
                      )}
                      {deposit_analysis.payment_method_saved && !deposit_analysis.stripe_details?.can_be_captured && (
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Alert severity="info" sx={{ mt: 1 }}>
                              <Typography variant="caption">
                                💳 Payment method sauvegardé - Charge off-session possible en fallback
                              </Typography>
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Section 2: Remboursements Renter */}
        <Grid item xs={12} md={6}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <PaymentsIcon sx={{ mr: 1, color: 'secondary.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  Remboursements Renter
                </Typography>
                {refund_analysis.has_refunds && (
                  <Chip 
                    label={formatAmount(refund_analysis.total_refunded)} 
                    color="secondary" 
                    size="small" 
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {!refund_analysis.has_refunds ? (
                <Alert severity="info">
                  Aucun remboursement effectué pour le moment
                </Alert>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="warning" icon={<InfoIcon />}>
                    <Typography variant="caption">
                      Total remboursé : <strong>{formatAmount(refund_analysis.total_refunded)}</strong>
                    </Typography>
                  </Alert>
                  <Table size="small">
                    <TableBody>
                      {refund_analysis.refund_details.map((refund, index) => (
                        <React.Fragment key={refund.payment_id}>
                          {index > 0 && (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Divider />
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell><strong>Paiement #{index + 1}</strong></TableCell>
                            <TableCell align="right">
                              <Chip label={refund.payment_method} size="small" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Montant original</TableCell>
                            <TableCell align="right">{formatAmount(refund.original_amount)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Montant remboursé</TableCell>
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                              {formatAmount(refund.refunded_amount)}
                            </TableCell>
                          </TableRow>
                          {refund.refunded_at && (
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell align="right">{formatDate(refund.refunded_at)}</TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Section 3: Payouts Ponctionnables Owner */}
        <Grid item xs={12} md={6}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <AttachMoneyIcon sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  Payouts Owner Ponctionnables
                </Typography>
                {payouts_analysis.owner.has_payouts && (
                  <Chip 
                    label={`${payouts_analysis.owner.pending_payouts.length} pending`} 
                    color="success" 
                    size="small" 
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {!payouts_analysis.owner.has_payouts ? (
                <Alert severity="info">
                  Aucun payout pending ponctionnable pour l'owner
                </Alert>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="success">
                    <Typography variant="caption">
                      Total ponctionnable : <strong>{formatPayoutAmount(payouts_analysis.owner.total_ponctionnable)}</strong>
                    </Typography>
                  </Alert>
                  <Box sx={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
                    <Table size="small">
                      <TableBody>
                      {payouts_analysis.owner.pending_payouts.map((payout, index) => (
                        <React.Fragment key={payout.payout_id}>
                          {index > 0 && (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Divider />
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell><strong>Payout #{index + 1}</strong></TableCell>
                            <TableCell align="right">
                              <Tooltip title={`Réservation ${payout.reservation_id?.substring(0, 8)}`}>
                                <Chip label="Pending" color="warning" size="small" />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Montant ponctionnable</strong></TableCell>
                            <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                              {formatPayoutAmount(payout.net_amount)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Date création</TableCell>
                            <TableCell align="right">{formatDate(payout.created_at)}</TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  </Box>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Section 4: Payouts Ponctionnables Renter */}
        <Grid item xs={12} md={6}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <AttachMoneyIcon sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  Payouts Renter Ponctionnables
                </Typography>
                {payouts_analysis.renter.has_payouts && (
                  <Chip 
                    label={`${payouts_analysis.renter.pending_payouts.length} pending`} 
                    color="info" 
                    size="small" 
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {!payouts_analysis.renter.has_payouts ? (
                <Alert severity="info">
                  Aucun payout pending ponctionnable pour le renter
                </Alert>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="info">
                    <Typography variant="caption">
                      Total ponctionnable : <strong>{formatPayoutAmount(payouts_analysis.renter.total_ponctionnable)}</strong>
                    </Typography>
                  </Alert>
                  <Box sx={{ maxHeight: 400, overflowY: 'auto', overflowX: 'hidden' }}>
                    <Table size="small">
                      <TableBody>
                      {payouts_analysis.renter.pending_payouts.map((payout, index) => (
                        <React.Fragment key={payout.payout_id}>
                          {index > 0 && (
                            <TableRow>
                              <TableCell colSpan={2}>
                                <Divider />
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow>
                            <TableCell><strong>Payout #{index + 1}</strong></TableCell>
                            <TableCell align="right">
                              <Chip label="Pending" color="warning" size="small" />
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell><strong>Montant ponctionnable</strong></TableCell>
                            <TableCell align="right" sx={{ color: 'info.main', fontWeight: 600 }}>
                              {formatPayoutAmount(payout.net_amount)}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Date création</TableCell>
                            <TableCell align="right">{formatDate(payout.created_at)}</TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                  </Box>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Section 5: Résumé des Sources de Recouvrement */}
        {max_recoverable.has_recoverable_funds && (
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.lighter' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'success.dark' }}>
                📊 Sources de Recouvrement Disponibles
              </Typography>
              <Grid container spacing={2}>
                {max_recoverable.sources.map((source, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'white' }}>
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {source.source === 'deposit_capture' && '💰 Caution (capture)'}
                            {source.source === 'deposit_off_session' && '💳 Caution (off-session)'}
                            {source.source === 'owner_payouts' && '📄 Payouts Owner'}
                            {source.source === 'renter_payouts' && '📄 Payouts Renter'}
                          </Typography>
                          {source.payouts_count && (
                            <Chip label={`${source.payouts_count} payout(s)`} size="small" />
                          )}
                        </Box>
                        <Typography variant="h6" sx={{ color: 'success.main', fontWeight: 600 }}>
                          {(source.source === 'owner_payouts' || source.source === 'renter_payouts') ? formatPayoutAmount(source.amount) : formatAmount(source.amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Méthode : {source.method}
                        </Typography>
                        {source.warning && (
                          <Alert severity="warning" sx={{ py: 0 }}>
                            <Typography variant="caption">{source.warning}</Typography>
                          </Alert>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          ℹ️ Cette analyse est calculée en temps réel et reflète l'état actuel des fonds disponibles. 
          Les montants peuvent varier si des opérations financières sont effectuées entre-temps.
        </Typography>
      </Box>
    </Paper>
  );
};

export default DisputeFinancialAnalysis;
